import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { notify } from '../../services/notification';
import { X, Search } from "lucide-react";

import "./dbtInvoiceScanStatus.scss";
import DbtInvoiceScanStatusTable from "./DbtInvoiceScanStatusTable";
import { constant } from "../../constants/constant";
import { getDbtInvoiceScanStatusAction, downloadDbtInvoiceScanStatusExcelAction } from "../../actions/dbtAction";
import DbtInvoiceSkeleton from "../../component/SkeletonLoader/DbtInvoiceSkeleton";
import { Image_Url } from "../../constants/imageUrl/ImageUrl";
import { isMoreThanThirtyDays } from "../../constants/dateTimeUtils/dateTimeUtils";

export default function DbtInvoiceScanStatus({ reportId }) {
  const dispatch = useDispatch();
  const pageSize = constant?.paginationCount || 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [invoiceList, setInvoiceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false); // For full page skeleton on first load
  const isInitialMount = useRef(true);
  const isApsTypeInitialMount = useRef(true); // Separate ref for apsType effect

  // Filter states
  const [searchField, setSearchField] = useState(""); // "customer_code" or "aps_code"
  const [searchValue, setSearchValue] = useState("");
  const [apsType, setApsType] = useState(""); // "", "primary", or "non-primary"
  const [scanStatus, setScanStatus] = useState(""); // "", "scanned", or "not_scanned"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Search field options for dropdown
  const searchFieldOptions = [
    { value: "customer_code", label: "Customer Code", type: "text" },
    { value: "aps_code", label: "APS Code", type: "number" },
    { value: "invoice_number", label: "Invoice Number", type: "text" },
    { value: "is_scanned", label: "Scan Status", type: "select" },
  ];

  // Scan status options for dropdown
  const scanStatusOptions = [
    { value: "true", label: "Scanned" },
    { value: "false", label: "Not Scanned" },
  ];

  // Get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / pageSize)),
    [totalCount, pageSize]
  );

  // Build query string from filters
  const buildQueryString = (overrideApsType = null) => {
    let query = "";
    if (searchField && searchValue.trim() && searchField !== "is_scanned") {
      query += `&${searchField}=${searchValue.trim()}`;
    }
    // Use override if provided, otherwise use state
    const apsTypeValue = overrideApsType !== null ? overrideApsType : apsType;
    if (apsTypeValue !== "") {
      query += `&aps_type=${apsTypeValue}`;
    }
    // Add is_scanned filter
    if (scanStatus) {
      query += `&is_scanned=${scanStatus}`;
    }
    if (fromDate) {
      query += `&from_date=${fromDate}`;
    }
    if (toDate) {
      query += `&to_date=${toDate}`;
    }
    return query;
  };

  const fetchInvoiceList = async (page = currentPage, queryParam = "") => {
    try {
      setLoading(true);

      const res = await dispatch(
        getDbtInvoiceScanStatusAction(page, pageSize, queryParam)
      );

      if (res?.status === 200) {
        const apiData = res?.data?.results || [];

        // Pass API data directly to table (uses snake_case keys)
        setInvoiceList(Array.isArray(apiData) ? apiData : []);
        const count = res?.data?.count || 0;
        setTotalCount(count);

        // Clamp current page if it went out of range
        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (page > lastPage) setCurrentPage(lastPage);
      } else {
        notify.error("Failed to load invoice data");
      }
    } catch (error) {
      notify.error("Failed to load invoice data");
      console.error("Error fetching invoice list:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false); // Turn off initial skeleton after first fetch
    }
  };

  // Initial load & pagination change
  // useEffect(() => {
  //   if (isInitialMount.current) {
  //     isInitialMount.current = false;
  //     fetchInvoiceList(currentPage, buildQueryString());
  //   } else {
  //     const query = buildQueryString();
  //     fetchInvoiceList(currentPage, query);
  //   }
  // }, [currentPage]);

  // Auto-fetch when apsType changes (like RetailerList pattern)
  // useEffect(() => {
  //   if (isApsTypeInitialMount.current) {
  //     isApsTypeInitialMount.current = false;
  //     return; // Skip API call on initial mount
  //   }
  //   // Reset to page 1 and fetch with new apsType
  //   if (currentPage !== 1) {
  //     setCurrentPage(1);
  //   } else {
  //     fetchInvoiceList(1, buildQueryString());
  //   }
  // }, [apsType]);

  // Auto-fetch when scanStatus changes
  // useEffect(() => {
  //   if (!scanStatus) return; // Skip if scanStatus is empty
  //   // Reset to page 1 and fetch with new scanStatus
  //   if (currentPage !== 1) {
  //     setCurrentPage(1);
  //   } else {
  //     fetchInvoiceList(1, buildQueryString());
  //   }
  // }, [scanStatus]);

  // Handle APS Type button click (like RetailerList handleRetailerTypeClick)
  const handleApsTypeClick = (type) => {
    setApsType(type);
  };

  // Handle From Date change - reset To Date if From Date changes
  const handleFromDateChange = (e) => {
    const newFromDate = e.target.value;
    setFromDate(newFromDate);
    // Reset toDate if it's before the new fromDate
    if (toDate && newFromDate && toDate < newFromDate) {
      setToDate("");
    }
  };

  // Handle To Date change
  const handleToDateChange = (e) => {
    const newToDate = e.target.value;
    if (fromDate && newToDate && !isMoreThanThirtyDays(fromDate, newToDate)) {
      notify.error("Date range should not be more than 30 days");
      return;
    }
    setToDate(newToDate);
  };

  // Check if any filter has value for search button (excluding apsType since it auto-fetches)
  const hasSearchableFilter = (searchField && searchValue.trim()) || scanStatus || (fromDate && toDate);

  // Check if any filter is active for clear button
  const hasActiveFilters = searchField || searchValue || apsType || scanStatus || fromDate || toDate;

  // Check if search button should be disabled
  const isSearchDisabled = loading || !hasSearchableFilter;

  const handleSearch = () => {
    // Validate date range
    if (fromDate && !toDate) {
      notify.error("Please select To Date");
      return;
    }
    if (toDate && !fromDate) {
      notify.error("Please select From Date first");
      return;
    }
    if (fromDate && toDate && !isMoreThanThirtyDays(fromDate, toDate)) {
      notify.error("Date range should not be more than 30 days");
      return;
    }

    const query = buildQueryString();
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      // fetchInvoiceList(1, query);
    }
  };

  const handleClearSearch = () => {
    setSearchField("");
    setSearchValue("");
    setApsType("");
    setScanStatus("");
    setFromDate("");
    setToDate("");

    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      // fetchInvoiceList(1, "");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSearchDisabled) {
      handleSearch();
    }
  };

  // Download Excel handler
  const handleDownloadExcel = async () => {
    try {
      if (fromDate && toDate && !isMoreThanThirtyDays(fromDate, toDate)) {
        notify.error("Date range should not be more than 30 days");
        return;
      }
      setDownloadLoading(true);
      const query = buildQueryString();

      // Pass query params and reportId to download API
      const response = await dispatch(downloadDbtInvoiceScanStatusExcelAction(query, reportId));

      if (response?.status === 200 && response?.data) {
        // Show success message from API response
        const message = response.data.message || 'Excel file will be sent to your email';
        notify.success(message);
      }
    } catch (error) {
      notify.error('Error downloading file');
      console.error('Error downloading file:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  // Handle search value change with validation for number fields
  const handleSearchValueChange = (e) => {
    const value = e.target.value;
    const inputType = getInputType();

    // For number type fields, only allow digits (no e, +, -, . etc.)
    if (inputType === "number") {
      // Remove any non-digit characters using regex
      const sanitizedValue = value.replace(/[^0-9]/g, "");
      setSearchValue(sanitizedValue);
    } else if (searchField === "customer_code") {
      // For customer_code, only allow digits and hyphens (e.g., 30765-345)
      const sanitizedValue = value.replace(/[^0-9-]/g, "");
      setSearchValue(sanitizedValue);
    } else {
      setSearchValue(value);
    }
  };

  // Get input type based on selected field
  const getInputType = () => {
    const field = searchFieldOptions.find(opt => opt.value === searchField);
    return field?.type || "text";
  };

  // Get placeholder based on selected field
  const getPlaceholder = () => {
    if (!searchField) return "Select a field first";
    const field = searchFieldOptions.find(opt => opt.value === searchField);
    return `Enter ${field?.label || ''}`;
  };

  // Show full page skeleton on initial load
  if (initialLoading) {
    return <DbtInvoiceSkeleton />;
  }

  return (
    <div className="dbt-invoice-wrapper">
      {/* Filter Section */}
      <section className="dbt-filter-card">
        {/* APS Type Buttons - Similar to RetailerList */}
        <div className="dbt-type-buttons">
          <button
            type="button"
            className={`dbt-type-btn ${apsType === "" ? "active" : ""}`}
            onClick={() => handleApsTypeClick("")}
          >
            All
          </button>
          <button
            type="button"
            className={`dbt-type-btn dbt-btn-primary ${apsType === "primary" ? "active" : ""}`}
            onClick={() => handleApsTypeClick("primary")}
          >
            Primary
          </button>
          <button
            type="button"
            className={`dbt-type-btn dbt-btn-non-primary ${apsType === "non-primary" ? "active" : ""}`}
            onClick={() => handleApsTypeClick("non-primary")}
          >
            Non Primary
          </button>
        </div>

        <div className="dbt-filter-row">
          {/* Dropdown + Input Combo */}
          <div className="dbt-filter-group dbt-search-combo">
            <label className="dbt-filter-label">Search By</label>
            <div className="dbt-combo-wrapper">
              <select
                className="dbt-filter-dropdown"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setSearchValue("");
                  if (e.target.value !== "is_scanned") {
                    setScanStatus("");
                  }
                }}
              >
                <option value="">Select Field</option>
                {searchFieldOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="dbt-input-with-icon">
                {/* Show dropdown for is_scanned, otherwise show text input */}
                {searchField === "is_scanned" ? (
                  <div className="dbt-select-wrapper">
                    <select
                      className="dbt-filter-dropdown dbt-scan-status-dropdown"
                      value={scanStatus}
                      onChange={(e) => {
                        setScanStatus(e.target.value);
                        // Auto-fetch when scan status changes
                        const query = buildQueryString();
                        const newQuery = query.replace(/&is_scanned=[^&]*/g, '') + (e.target.value ? `&is_scanned=${e.target.value}` : '');
                        if (currentPage !== 1) {
                          setCurrentPage(1);
                        } else {
                          // fetchInvoiceList(1, newQuery);
                        }
                      }}
                    >
                      <option value="">Select Status</option>
                      {scanStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {(searchField || scanStatus) && (
                      <button
                        className="dbt-select-clear"
                        onClick={() => {
                          setSearchField("");
                          setSearchValue("");
                          setScanStatus("");
                        }}
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      inputMode={getInputType() === "number" ? "numeric" : "text"}
                      pattern={getInputType() === "number" ? "[0-9]*" : undefined}
                      className="dbt-filter-input"
                      placeholder={getPlaceholder()}
                      value={searchValue}
                      onChange={handleSearchValueChange}
                      onKeyPress={handleKeyPress}
                      disabled={!searchField}
                    />
                    {(searchField || searchValue) && (
                      <button
                        className="dbt-input-clear"
                        onClick={() => {
                          setSearchField("");
                          setSearchValue("");
                          setScanStatus("");
                        }}
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* From Date */}
          <div className="dbt-filter-group">
            <label className="dbt-filter-label">From Date <span className="dbt-mandatory">*</span></label>
            <input
              type="date"
              className="dbt-filter-input dbt-date-input"
              value={fromDate}
              max={getCurrentDate()}
              onChange={handleFromDateChange}
            />
          </div>

          {/* To Date */}
          <div className="dbt-filter-group">
            <label className="dbt-filter-label">To Date <span className="dbt-mandatory">*</span></label>
            <input
              type="date"
              className={`dbt-filter-input dbt-date-input ${!fromDate ? 'disabled-look' : ''}`}
              value={toDate}
              min={fromDate || ""}
              max={getCurrentDate()}
              onChange={handleToDateChange}
              disabled={!fromDate}
              title={!fromDate ? "Please select From Date first" : ""}
            />
          </div>

          {/* Search and Clear Buttons */}
          <div className="dbt-filter-group dbt-filter-buttons">
            <label className="dbt-filter-label">&nbsp;</label>
            <div className="dbt-filter-actions">
              {/* Search button commented out for now
              <button
                className="dbt-search-btn"
                onClick={handleSearch}
                disabled={isSearchDisabled}
                title={isSearchDisabled && !loading ? "Please select at least one filter" : ""}
              >
                <Search size={16} />
                Search
              </button>
              */}
              {hasActiveFilters && (
                <button
                  className="dbt-clear-btn"
                  onClick={handleClearSearch}
                  disabled={loading}
                  aria-label="Clear filters"
                  title="Clear all filters"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Download Excel Button */}
          <div className="dbt-filter-group dbt-download-group">
            <label className="dbt-filter-label">&nbsp;</label>
            <button
              className="dbt-download-excel-btn"
              onClick={handleDownloadExcel}
              disabled={downloadLoading || loading || !fromDate || !toDate}
              title={!fromDate || !toDate ? "Please select both From Date and To Date" : "Download Excel"}
            >
              <img src={Image_Url.EXCEL_LOGO} className="dbt-excel-icon" alt="Excel" />
              {downloadLoading ? "Downloading..." : "Download Excel"}
            </button>
          </div>
        </div>
      </section>

      {/* <DbtInvoiceScanStatusTable
        data={invoiceList}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        totalCount={totalCount}
        pageSize={pageSize}
      /> */}
    </div>
  );
}
